import { DOCUMENT } from "@angular/common";
import { effect, inject, Injectable } from "@angular/core";

import { AssessmentType } from "@intocare/assessments/lib/assessment";
import { ENVIRONMENT_TOKEN } from "@intocare/environments";
import { CONTEXT_TOKEN, ContextNotInitializedError } from "@intocare/util";

interface MatomoEvent {
	event: string;
	[key: string]: unknown;
}

type VariableKey =
	"intocareUserId" | "hq" | "organisation" | "function" | "region" | "eventCategory" | "eventAction" | "eventName";

const MATOMO_EVENT_TRIGGER_NAME = "EventFromCode";

type MatomoObservationEventData = {
	eventCategory: "Observation";
	eventAction: string;
	eventName:
		| "Creation started"
		| "Creation completed"
		| "Creation validation failed"
		| "Edit started"
		| "Edit validation failed"
		| "Edit completed"
		| "OBS Observation linked"
		| "OBS Observation unlinked";
};

type MatomoCommunicationEventData = {
	eventCategory: "Communication";
	eventAction: string;
	eventName: "Creation completed" | "Edit completed" | "Message opened";
};

type MatomoFUPEventData = {
	eventCategory: "Follow-up plan";
	eventAction: string;
	eventName:
		| "FUP Creation started"
		| "FUP Creation completed"
		| "FUP Creation validation failed"
		| "FUP Edit started"
		| "FUP Edit validation failed"
		| "FUP Edit completed"
		| "FUP Observation linked"
		| "FUP Observation unlinked"
		| "FUP Eval Creation completed"
		| "FUP Eval Edit draft completed"
		| "FUP Eval Edit finalized completed";
};

type MatomoKatzEventData = {
	eventCategory: "Resident information";
	eventAction: "KATZ";
	eventName: "KATZ review started in Timeline" | "KATZ undo" | "KATZ review started" | "KATZ registration";
};

type MatomoAssessmentEventData = {
	eventCategory: "Assessment";
	eventAction: `Assessment ${AssessmentType}`;
	eventName:
		| "Assessment Creation started"
		| "Assessment Creation completed"
		| "Assessment Downloaded"
		| "Assessment Upload started"
		| "Assessment Upload attachment"
		| "Assessment Download attachment"
		| "Assessment Delete attachment"
		| "Assessment Archived"
		| "Assessment Next assessment date set"
		| "Assessment Next assessment date deleted";
};

type MatomoEventData =
	| MatomoObservationEventData
	| MatomoCommunicationEventData
	| MatomoFUPEventData
	| MatomoKatzEventData
	| MatomoAssessmentEventData;

type MatomoVariable = {
	[K in VariableKey]: Record<K, string>;
}[VariableKey];

type MatomoItem = MatomoEvent | MatomoVariable;

// Extend the Window interface to include the _paq property used by Matomo.
declare const window: Window & { _paq?: unknown[] };

@Injectable({ providedIn: "root" })
export class MatomoService {
	private readonly environment = inject(ENVIRONMENT_TOKEN);
	private readonly document = inject(DOCUMENT);
	private readonly contextService = inject(CONTEXT_TOKEN);

	constructor() {
		effect(() => {
			try {
				const context = this.contextService.context();

				this.setVariable("function", context.mainFunction?.code);

				if (this.environment.platform === "home") {
					const { home, federatedEntity } = context.currentSite;

					this.setVariable("intocareUserId", `${context.hq}.${home}.${context.user.id}`);
					this.setVariable("organisation", `${context.hq}.${home}`);
					this.setVariable("hq", context.hq);
					this.setVariable("region", federatedEntity);
				} else {
					this.setVariable("intocareUserId", `${context.hq}.${context.user.id}`);
					this.setVariable("hq", context.hq);
				}
			} catch (error) {
				if (error instanceof ContextNotInitializedError) {
					return;
				}

				throw error;
			}
		});
	}

	private get dataLayer(): MatomoItem[] {
		if (!this.document.defaultView) {
			throw new Error("Window is not available");
		}

		const matomoWindowProperty = "_mtm";

		return (this.document.defaultView[matomoWindowProperty] = this.document.defaultView[matomoWindowProperty] || []);
	}

	private isDisabled(): boolean {
		return !this.environment.MATOMO;
	}

	public setVariable(key: VariableKey, value: string | undefined): void {
		if (this.isDisabled() || !value) {
			return;
		}

		this.dataLayer.push({ [key]: value } as MatomoVariable);
	}

	public load(): void {
		if (this.isDisabled()) {
			return;
		}

		const matomoStartEvent = { "mtm.startTime": new Date().getTime(), event: "mtm.Start" };
		this.dataLayer.push(matomoStartEvent);

		const matomoScript = this.document.createElement("script");
		const firstDocumentScriptElement = this.document.getElementsByTagName("script")[0];

		matomoScript.async = true;
		matomoScript.src = this.environment.MATOMO.URL;
		firstDocumentScriptElement?.parentNode?.insertBefore(matomoScript, firstDocumentScriptElement);
	}

	public trackEvent(eventData: MatomoEventData): void {
		if (this.isDisabled()) {
			return;
		}
		this.dataLayer.push({ event: MATOMO_EVENT_TRIGGER_NAME, ...eventData });
	}

	public trackDownloadByName(name: string): void {
		if (this.isDisabled()) {
			return;
		}
		// Matomo's recommended way to track downloads is to push an event with the "trackLink" action and the "download" type.
		// https://developer.matomo.org/guides/tracking-javascript-guide#tracking-downloads-outgoing-links-and-searches
		window._paq?.push(["trackLink", name, "download"]);
	}
}
